import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { ArrowBigLeft, ArrowBigRight, Loader, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { useCallback } from "use-memo-one";

import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { getFragmentData } from "@/gen";
import { useImageUpload } from "@/hooks/use-image-upload";
import { UpdateVideoForm_Video, useVideo } from "@/routes/video/queries";
import { cn } from "@/utils/cx";
import { generateImageUrl } from "@/utils/image";

import { VideoIdInput } from "./VideoInput";
import { ArticleInput } from "./articleInput";

type StoryItems = UseFieldArrayReturn<ArticleInput, "items", "id">;
type Form = UseFormReturn<ArticleInput>;

const PickStoryItemModal = ({
    onStartSelectImageFile,
    storyItems,
    onFinished,
}: {
    onStartSelectImageFile: () => void;
    storyItems: StoryItems;
    onFinished: () => void;
}) => {
    const [videoSelected, setVideoSelected] = useState(false);
    const onImageSelected = useCallback(
        e => {
            setVideoSelected(false);
            e.preventDefault();
            onStartSelectImageFile();
            onFinished();
        },
        [onFinished, onStartSelectImageFile]
    );
    const onVideoSelected = useCallback(e => {
        e.preventDefault();
        setVideoSelected(true);
    }, []);

    const [videoId, setVideoId] = useState<string | null>(null);
    const onVideoIdChange = useCallback(id => {
        setVideoId(id);
    }, []);

    const onAddVideo = useCallback(
        e => {
            e.preventDefault();
            if (videoId != null) {
                storyItems.append({
                    rank: storyItems.fields.length + 1,
                    content: {
                        video: {
                            id: videoId,
                        },
                    },
                });
                onFinished();
            }
        },
        [onFinished, storyItems, videoId]
    );

    return (
        <div className="bg-brand-blue flex min-w-96 flex-col justify-center gap-4 rounded-sm p-4">
            {!videoSelected && (
                <>
                    <h1 className="text-center">Select story media:</h1>
                    <div className="m-auto flex w-1/2 justify-between">
                        <button className="bg-input rounded-sm p-2" onClick={onImageSelected}>
                            Image
                        </button>
                        <button className="bg-input rounded-sm p-2" onClick={onVideoSelected}>
                            Video
                        </button>
                    </div>
                </>
            )}
            {videoSelected && (
                <div className="flex flex-col gap-2">
                    <VideoIdInput onVideoIdChange={onVideoIdChange} videoId={videoId} />
                    <Button
                        variant="default"
                        className="rounded-sm p-2"
                        disabled={videoId == null}
                        onClick={onAddVideo}
                    >
                        Add
                    </Button>
                </div>
            )}
        </div>
    );
};

export const StoryEdit = ({ storyItems, form }: { storyItems: StoryItems; form: Form }) => {
    const [selectMediaModalOpen, setSelectMediaModalOpen] = useState(false);
    const { startSelectFile, inputProps, isUploading } = useImageUpload({
        onUploadFinished: imageId =>
            storyItems.append({
                rank: storyItems.fields.length + 1,
                content: {
                    image: {
                        duration: 5000,
                        id: imageId,
                    },
                },
            }),
    });

    return (
        <>
            <Input {...inputProps} />
            <div className="flex w-full flex-row flex-wrap items-start">
                {form.watch("items").map((item, index) => (
                    <StoryImageEdit
                        key={item.id}
                        index={index}
                        storyItems={storyItems}
                        form={form}
                    />
                ))}
                <div className="flex h-[400px] w-[200px] items-center justify-center border-4 border-dotted">
                    <Popover
                        open={selectMediaModalOpen}
                        onOpenChange={open => setSelectMediaModalOpen(open)}
                    >
                        <PopoverTrigger onClick={() => setSelectMediaModalOpen(true)} asChild>
                            <button type="button" disabled={isUploading}>
                                {isUploading ? <Loader size={128} /> : <Plus size={128} />}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="center" side="right">
                            <PickStoryItemModal
                                onFinished={() => {
                                    setSelectMediaModalOpen(false);
                                }}
                                onStartSelectImageFile={startSelectFile}
                                storyItems={storyItems}
                            />
                            <PopoverArrow fill="var(--color-brand-blue)" />
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        </>
    );
};

export const StoryImageEdit = ({
    index,
    storyItems,
    form,
}: {
    index: number;
    storyItems: StoryItems;
    form: Form;
}) => {
    return (
        <div className="flex w-[200px] flex-col content-between items-center gap-4 p-3">
            <div className="p-15 flex flex-row gap-2">
                <Button
                    size="icon"
                    variant="outline"
                    onClick={e => {
                        e.preventDefault();
                        if (index !== 0) {
                            storyItems.swap(index, index - 1);
                        }
                    }}
                >
                    <ArrowBigLeft />
                </Button>
                <Button
                    size="icon"
                    variant="outline"
                    onClick={e => {
                        e.preventDefault();
                        if (index !== storyItems.fields.length - 1) {
                            storyItems.swap(index, index + 1);
                        }
                    }}
                >
                    <ArrowBigRight />
                </Button>
                <Button
                    size="icon"
                    variant="destructive"
                    onClick={e => {
                        e.preventDefault();
                        storyItems.remove(index);
                    }}
                >
                    <Trash2 />
                </Button>
            </div>
            <div className="flex-[4]">
                <FormField
                    control={form.control}
                    name={`items.${index}.content.image.id`}
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <StoryImageInput
                                    imageId={field.value}
                                    onUploadFinished={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={`items.${index}.content.video.id`}
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <StoryVideoInput videoId={field.value} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={`items.${index}.content.image.duration`}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                            {field.value != null && (
                                <>
                                    <FormLabel>Duration&nbsp;(ms)</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            placeholder="defaults to 5000 ms"
                                            {...field}
                                            onChange={e => field.onChange(e.target.valueAsNumber)}
                                        />
                                    </FormControl>
                                </>
                            )}
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </div>
    );
};

const StoryImageInput = ({
    imageId,
    onUploadFinished,
}: {
    imageId: string | null;
    onUploadFinished: (imageId: string) => void;
}) => {
    const { startSelectFile, inputProps, isUploading } = useImageUpload({
        onUploadFinished,
    });

    const width = 300;
    const ratio = 9 / 16;

    if (imageId == null) {
        return null;
    }

    return (
        <div>
            <Input {...inputProps} />
            <div className={cn("group relative")}>
                <Button
                    type="button"
                    variant="outline"
                    disabled={isUploading}
                    onClick={() => {
                        startSelectFile();
                    }}
                    className={cn("absolute right-4 top-4 group-hover:block", {
                        hidden: !isUploading,
                    })}
                    size="icon"
                >
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil />}
                </Button>

                <img
                    src={generateImageUrl({
                        imageId,
                        width,
                        height: width / ratio,
                    })}
                    className="block w-full"
                />
                {isUploading && (
                    <div className="flex h-48 w-1/3 items-center justify-center rounded-lg border-2 border-dashed p-4">
                        <p>Loading...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const StoryVideoInput = ({ videoId }: { videoId: string }) => {
    const width = 300;
    const height = ((width * 16) / 9).toFixed(0);
    const video = useVideo(videoId);

    if (videoId == null) {
        return null;
    }

    const videoData = getFragmentData(UpdateVideoForm_Video, video.video);

    const imageThumbnailUrl = `https://image.mux.com/${videoData?.muxPlaybackId}/thumbnail.png?width=${width}&height=${height}`;

    return (
        <div>
            <div className={cn("group relative")}>
                <img src={imageThumbnailUrl} className="block w-full" />
            </div>
        </div>
    );
};
